<template>

  <!-- Selected airlines section (always visible) -->
  <div class="space-y-3">
    <SectionDivider title="Selected airlines" icon="bi-check-circle-fill"/>

    <div v-if="selectedAirlines.length === 0" class="text-sm text-base-content/50 px-1">
      No airlines selected. Use the search below to add airlines.
    </div>

    <div v-else :class="['grid gap-3',
      props.modal ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
    ]"
    >
      <div
          v-for="airline in selectedAirlines"
          :key="airlineId(airline)"
          class="flex items-center gap-2 p-2 rounded-lg border border-base-200 bg-base-100 min-w-0"
      >
        <div
            class="w-9 h-9 flex items-center justify-center rounded bg-white border border-base-200 shrink-0 overflow-hidden">
          <img
              :src="logoUrl(airline)"
              alt=""
              class="w-full h-full object-contain p-0.5"
              @error="(e) => { e.target.src = '/img/default_airline.svg' }"
          />
        </div>

        <div class="text-sm min-w-0 flex-1 overflow-hidden">
          <p class="font-medium leading-tight truncate">{{airline.name ?? airline}}</p>
          <p class="text-xs text-base-content/50 truncate">{{airline.id}}</p>
        </div>

        <button
            type="button"
            class="btn btn-ghost btn-xs btn-circle shrink-0 text-primary"
            title="Remove"
            @click="toggleAirline(airline)"
        >
          <i class="bi bi-x-lg text-xs"></i>
        </button>
      </div>
    </div>
  </div>

  <!-- Search airlines section -->
  <div class="space-y-4" v-if="!hideSearch">
    <SectionDivider title="Search airlines" icon="bi-binoculars-fill"/>

    <!-- Search input -->
    <div class="relative">
      <input
          v-model="searchQuery"
          type="text"
          placeholder="Search airline by name or code…"
          class="input input-bordered w-full pl-10"
      />
      <i class="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50 pointer-events-none"></i>
      <span
          v-if="iataStore.airlinesLoading"
          class="loading loading-spinner loading-xs absolute right-3 top-1/2 -translate-y-1/2"
      ></span>
    </div>

    <!-- Search results – fixed-height container prevents dialog from jumping -->
    <div class="min-h-[13rem] max-h-[20rem] overflow-y-auto">
      <div
          v-if="searchQuery && visibleAirlines.length > 0"
          :class="[
            'grid gap-3',
            props.modal
              ? 'grid-cols-1'
              : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
          ]"
      >
        <div
            v-for="airline in visibleAirlines"
            :key="airline.id"
            class="flex items-center gap-2 p-2 rounded-lg border cursor-pointer min-w-0"
            :class="isSelected(airline.id) ? 'border-primary bg-primary/5' : 'border-base-200 hover:bg-base-100'"
            @click="toggleAirline(airline)"
        >
          <div
              class="w-9 h-9 flex items-center justify-center rounded bg-white border border-base-200 shrink-0 overflow-hidden">
            <img
                :src="logoUrl(airline)"
                alt=""
                class="w-full h-full object-contain p-0.5"
                @error="(e) => { e.target.src = '/img/default_airline.svg' }"
            />
          </div>

          <div class="text-sm min-w-0 flex-1 overflow-hidden">
            <p class="text-base-content font-medium leading-tight truncate">{{airline.name}}</p>
            <p class="text-xs text-base-content/50 truncate">{{airline.id}}</p>
          </div>
        </div>
      </div>

      <div
          v-else-if="searchQuery && !iataStore.airlinesLoading && visibleAirlines.length === 0"
          class="text-sm text-base-content/50 pt-2"
      >
        No matching airlines found.
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue'
import {useIataStore} from '@/stores/iataStore.js'
import SectionDivider from '@/components/atoms/SectionDivider.vue'

const iataStore = useIataStore()

function logoUrl(airline) {
  return airline?.small_logo || airline?.logo || ''
}

const props = defineProps({
  hideSearch: {type: Boolean, default: false},
  excludeIds: {type: Array, default: () => []},
  modal: {type: Boolean, default: false},
})

const visibleAirlines = computed(() => {
  if (!props.excludeIds.length) return iataStore.airlines
  return iataStore.airlines.filter(a => !props.excludeIds.includes(a.id))
})

const requestAirlines = defineModel()

const searchQuery = ref('')
const selectedAirlines = ref([])

// ── Init from model (supports edit-mode pre-population) ───────────────────────
onMounted(() => {
  selectedAirlines.value = [...(requestAirlines.value || [])]
})

// ── Helpers ──────────────────────────────────────────────────────────────────
function airlineId(a) {
  return a?.id ?? a
}

function isSelected(id) {
  return selectedAirlines.value.some(a => airlineId(a) === id)
}

function toggleAirline(airline) {
  const id = airlineId(airline)
  const idx = selectedAirlines.value.findIndex(a => airlineId(a) === id)
  if (idx >= 0) {
    selectedAirlines.value.splice(idx, 1)
  } else {
    selectedAirlines.value.push(airline)
    searchQuery.value = ''
  }
  requestAirlines.value = [...selectedAirlines.value]
}

// ── Debounced search ─────────────────────────────────────────────────────────
let debounceTimer = null

watch(searchQuery, (val) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  const q = (val ?? '').trim()
  if (!q) {
    iataStore.searchAirlines('')
    return
  }
  debounceTimer = setTimeout(() => {
    iataStore.searchAirlines(q)
  }, 350)
})
</script>
